import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stack,
  Divider,
  alpha,
  useTheme,
  Icon,
  Paper,
} from "@mui/material";
import { CURRENCY, OrderDiscount } from "../../types/pos.types";
import { DeliveryInfo } from "../DeliveryForm/DeliveryForm";

interface OrderItem {
  modifierDetails: any;
  id: string;
  name: string;
  price: number;
  basePrice: number;
  quantity: number;
  modifiers?: Record<string, string | string[]>;
}

interface BillSummaryDialogProps {
  open: boolean;
  onClose: () => void;
  onPrint: () => void;
  items: OrderItem[];
  subtotal: number;
  tax: number;
  total: number;
  paymentMethod: string;
  amountPaid?: number;
  change?: number;
  discount?: OrderDiscount | null;

  // Receipt Details
  orderId?: string;
  orderType?: string | null;
  tableNumber?: string;
  serverName?: string;
  timestamp?: Date;
  deliveryInfo: DeliveryInfo | null;

}

export const BillSummaryDialog: React.FC<BillSummaryDialogProps> = ({
  open,
  onClose,
  onPrint,
  items,
  subtotal,
  tax,
  total,
  paymentMethod,
  amountPaid,
  change,
  discount,
  deliveryInfo,
  orderId = "RFF-INV-000542",
  orderType = "Dine-In",
  tableNumber,
  serverName = "Cashier Name",
  timestamp = new Date(),
}) => {
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          backgroundColor: alpha(theme.palette.background.paper, 0.95),
          backdropFilter: "blur(10px)",
          overflow: "hidden",
          minWidth: 480,
        },
      }}
    >
      <Box
        sx={{
          backgroundColor: "#1E8078",
          py: 3,
          px: 3,
          color: "white",
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Icon sx={{ fontSize: 28 }}>receipt_long</Icon>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Order Summary
        </Typography>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        {/* Business & Order Info */}
        <Box textAlign="center" mb={3}>
          <Typography variant="h6" fontWeight="bold">
            {localStorage.getItem("storeName")}
          </Typography>
          <Typography variant="body2">
            Store ID: {localStorage.getItem("storeDisplayId") || "N/A"}
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2">
            Order #: <strong>{orderId}</strong>
          </Typography>
          <Typography variant="body2">
            Time: <strong>{timestamp.toLocaleString()}</strong>
          </Typography>
          <Typography variant="body2">
            Order Type: <strong>{orderType}</strong>
          </Typography>
          {tableNumber && (
            <Typography variant="body2">
              Table: <strong>{tableNumber}</strong>
            </Typography>
          )}
        </Box>
        {/* Show Delivery Info if order type is delivery */}
        {orderType === "delivery" && deliveryInfo && (
          <>
            <Typography
              variant="subtitle1"
              sx={{
                mb: 2,
                color: "#1E8078",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Icon>local_shipping</Icon>
              Delivery Information
            </Typography>

            <Paper
              elevation={0}
              sx={{
                backgroundColor: alpha("#1E8078", 0.05),
                border: `1px solid ${alpha("#1E8078", 0.1)}`,
                borderRadius: 2,
                p: 2,
                mb: 3,
              }}
            >
              <Stack spacing={1}>
                <Typography variant="body2">
                  <strong>Name:</strong> {deliveryInfo.name}
                </Typography>
                <Typography variant="body2">
                  <strong>Phone:</strong> {deliveryInfo.phone}
                </Typography>
                <Typography variant="body2">
                  <strong>Area:</strong> {deliveryInfo.area}
                </Typography>
                <Typography variant="body2">
                  <strong>Address:</strong> {deliveryInfo.address}
                </Typography>
              </Stack>
            </Paper>
          </>
        )}

        {/* Order Items */}
        <Box mb={3}>
          <Typography
            variant="subtitle1"
            sx={{
              mb: 2,
              color: "#1E8078",
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Icon sx={{ fontSize: 20 }}>shopping_cart</Icon>
            Order Items
          </Typography>
          <Paper
            elevation={0}
            sx={{
              backgroundColor: alpha("#1E8078", 0.03),
              borderRadius: 3,
              border: `1px solid ${alpha("#1E8078", 0.1)}`,
            }}
          >
            <Stack spacing={2} sx={{ p: 2 }}>
              {items.map((item) => (
                <React.Fragment key={item.id}>
                  <Box>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography sx={{ fontWeight: 500 }}>{item.name}</Typography>
                      <Typography sx={{ fontWeight: 500 }}>
                        {CURRENCY} {(item.price * item.quantity).toFixed(3)}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {item.quantity} x {CURRENCY} {item.basePrice.toFixed(3)}
                    </Typography>

                    {/* Show Modifiers */}
                    {item.modifierDetails?.map((mod: { name: string | number | bigint | boolean | React.ReactElement<unknown, string | React.JSXElementConstructor<any>> | Iterable<React.ReactNode> | React.ReactPortal | Promise<string | number | bigint | boolean | React.ReactPortal | React.ReactElement<unknown, string | React.JSXElementConstructor<any>> | Iterable<React.ReactNode> | null | undefined> | null | undefined; price: number; }, i: React.Key | null | undefined) => (
                      <Typography key={i} variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                        + {mod.name} (+{CURRENCY} {mod.price.toFixed(3)})
                      </Typography>
                    ))}
                  </Box>
                  <Divider sx={{ borderColor: alpha("#1E8078", 0.1) }} />
                </React.Fragment>
              ))}
            </Stack>
          </Paper>
        </Box>

        {/* Summary */}
        <Box mb={3}>
          <Typography
            variant="subtitle1"
            sx={{
              mb: 2,
              color: "#1E8078",
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Icon sx={{ fontSize: 20 }}>calculate</Icon>
            Summary
          </Typography>
          <Paper
            elevation={0}
            sx={{
              backgroundColor: alpha("#1E8078", 0.03),
              borderRadius: 3,
              border: `1px solid ${alpha("#1E8078", 0.1)}`,
              p: 2,
            }}
          >
            <Stack spacing={2}>
              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">Subtotal</Typography>
                <Typography>BHD {subtotal.toFixed(3)}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">VAT (10%)</Typography>
                <Typography>BHD {tax.toFixed(3)}</Typography>
              </Stack>
              {discount && discount.value > 0 && (
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">
                    Discount ({discount.type})
                  </Typography>
                  <Typography color="error">
                    - BHD{" "}
                    {discount.type === "PERCENTAGE"
                      ? ((subtotal + tax) * discount.value / 100).toFixed(3)
                      : discount.value.toFixed(3)}
                  </Typography>
                </Stack>
              )}
              <Divider sx={{ borderColor: alpha("#1E8078", 0.1) }} />
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="h6">Total</Typography>
                <Typography variant="h6" color="#1E8078">
                  BHD {total.toFixed(3)}
                </Typography>
              </Stack>
            </Stack>
          </Paper>
        </Box>

        {/* Payment Details */}
        <Box mb={3}>
          <Typography
            variant="subtitle1"
            sx={{
              mb: 2,
              color: "#1E8078",
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Icon sx={{ fontSize: 20 }}>payments</Icon>
            Payment Details
          </Typography>
          <Paper
            elevation={0}
            sx={{
              backgroundColor: alpha("#1E8078", 0.03),
              borderRadius: 3,
              border: `1px solid ${alpha("#1E8078", 0.1)}`,
              p: 2,
            }}
          >
            <Stack spacing={2}>
              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">Payment Method</Typography>
                <Typography textTransform="capitalize">
                  {paymentMethod}
                </Typography>
              </Stack>
              {amountPaid !== undefined && (
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">Amount Paid</Typography>
                  <Typography fontWeight="bold">
                    BHD {amountPaid.toFixed(3)}
                  </Typography>
                </Stack>
              )}
              {change !== undefined && change > 0 && (
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">Change Due</Typography>
                  <Typography color="#1E8078" fontWeight="bold">
                    BHD {change.toFixed(3)}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Paper>
        </Box>

        {/* Footer */}
        <Box textAlign="center" mt={3}>
          <Typography variant="body2">Thank you for your visit!</Typography>
          <Typography variant="caption" color="text.secondary">
            Please come again.
          </Typography>
        </Box>
      </DialogContent>

      <Box
        sx={{
          p: 3,
          backgroundColor: alpha(theme.palette.background.paper, 0.5),
          borderTop: `1px solid ${alpha("#1E8078", 0.1)}`,
        }}
      >
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              borderColor: alpha("#1E8078", 0.3),
              color: "#1E8078",
              "&:hover": {
                borderColor: "#1E8078",
                backgroundColor: alpha("#1E8078", 0.05),
              },
            }}
          >
            Close
          </Button>
        </Stack>
      </Box>
    </Dialog>
  );
};